// Minimal fuzzy matcher — no library. Subsequence match with scoring boosts.
// Score semantics:
//   0       — no match (one or more query chars missing in order)
//   (0..1)  — partial subsequence match
//   1       — perfect/very strong match (full prefix or exact equality)

const WORD_BOUNDARY_RE = /[\s/_\-.:]/;

export function fuzzyScore(query: string, target: string): number {
    if (!query) return 0.5; // empty query treated as neutral score
    if (!target) return 0;

    const q = query.toLowerCase();
    const t = target.toLowerCase();

    // Exact / prefix shortcuts
    if (q === t) return 1;
    if (t.startsWith(q)) return 0.95;

    let qi = 0;
    let score = 0;
    let consecutive = 0;
    let matched = 0;

    for (let ti = 0; ti < t.length && qi < q.length; ti++) {
        const tc = t[ti];
        if (tc === q[qi]) {
            matched++;
            // Base point per matched char
            let charScore = 1;
            // Word boundary bonus
            if (ti === 0 || WORD_BOUNDARY_RE.test(t[ti - 1])) {
                charScore += 1;
            }
            // Consecutive char bonus
            if (consecutive > 0) {
                charScore += consecutive;
            }
            score += charScore;
            consecutive++;
            qi++;
        } else {
            consecutive = 0;
        }
    }

    if (qi < q.length) return 0; // not all query chars matched

    // Normalize: maximum theoretical score ~ q.length * (1 + 1 + q.length-1) = q.length * (q.length + 1)
    const maxScore = q.length * (q.length + 1);
    let normalized = score / maxScore;

    // Length penalty: shorter targets that match are stronger signals
    const lengthRatio = matched / t.length;
    normalized = normalized * 0.7 + lengthRatio * 0.3;

    // Clamp to (0, 0.94] — reserve top scores for exact/prefix matches
    if (normalized > 0.94) normalized = 0.94;
    if (normalized <= 0) normalized = 0.01;
    return normalized;
}

export interface FuzzyMatch<T> {
    item: T;
    score: number;
}

/**
 * Fuzzy-filter items by extracting a search string via getKey.
 * Returns matched items sorted by score descending.
 * If multiple keys are needed, return them joined (e.g. `${summary} ${path}`).
 */
export function fuzzyFilter<T>(
    query: string,
    items: T[],
    getKey: (item: T) => string,
): FuzzyMatch<T>[] {
    const q = query.trim();
    if (!q) {
        return items.map((item) => ({ item, score: 0.5 }));
    }
    const out: FuzzyMatch<T>[] = [];
    for (const item of items) {
        const key = getKey(item);
        const score = fuzzyScore(q, key);
        if (score > 0) out.push({ item, score });
    }
    out.sort((a, b) => b.score - a.score);
    return out;
}
